/**
 * Idle sign-out engine (PD-16414).
 *
 * Signs a user out after a tenant-configured stretch without real input. The
 * policy (instance setting `security.idle_timeout`) is enforced on the client:
 * the server has no idea whether anyone is at the keyboard, because API
 * traffic and background polling keep flowing whether or not they are. So the
 * clock here moves only on genuine input, reported by the React hook in
 * libs/web-ui.
 *
 * The last-activity time lives in localStorage, shared by every tab of the
 * app. Activity in one tab keeps the others alive, the first tab to reach the
 * limit marks the record `timedOut` so tabs that hear it follow instead of
 * revoking themselves (see `_timeOut` for the one case where two tabs both
 * revoke), and a page that loads with a stale record (laptop lid closed, a
 * crashed tab, the native CRM reloading after a long background stay) signs
 * out the moment it starts.
 *
 * This file is deliberately free of DOM and React: storage, clock and timers
 * are injected, which is what lets the timing rules be tested with fake timers.
 */

import {
	IDLE_TIMEOUT_MAX_MINUTES,
	IDLE_TIMEOUT_MIN_MINUTES,
} from '@supply-mover-2-0/shared';

/** Logout reason recorded for an idle sign-out (see `AuthService.logout`). */
export const IDLE_TIMEOUT_REASON = 'idle_timeout';

/** localStorage keys, one per app (the apps run on separate origins anyway). */
export const IDLE_ACTIVITY_STORAGE_KEYS = {
	/**
	 * `workd:`-prefixed on purpose: every CRM sign-out sweeps `workd:` keys,
	 * so the record never outlives the session it measured.
	 */
	crm: 'workd:idle:crm',
	/** Removed explicitly by the storefront auth store's `reset()`. */
	storefront: 'workd:idle:storefront',
} as const;

/** Longest warning shown before the sign-out. */
const MAX_WARNING_LEAD_MS = 60_000;

/**
 * Minimum gap between two writes of the shared record. Pointer movement
 * fires dozens of events a second; writing each one would flood localStorage
 * and every other tab's `storage` listener. Five seconds of slack against a
 * limit measured in minutes is immaterial.
 */
export const IDLE_ACTIVITY_WRITE_THROTTLE_MS = 5_000;

/** The shared record, JSON-encoded under the app's storage key. */
export interface IdleActivityRecordModel {
	/** Epoch ms of the last real input in any tab. */
	at: number;
	/** Set by the tab that signed the user out, so other tabs follow it. */
	timedOut?: boolean;
}

/**
 * How much a piece of input says about the user (PD-16414).
 *
 * - `passive`: pointer movement, scrolling, touch and focus changes. Keeps
 *   the session alive, but never answers the warning: a hand moving toward
 *   Stay signed in would otherwise close the dialog before the click, and the
 *   click would land on whatever the dialog was covering.
 * - `deliberate`: Stay signed in, a click on the dialog, or a keypress (which
 *   the page consumes while the warning shows). Answers the warning.
 */
export type IdleActivityKindModel = 'passive' | 'deliberate';

/** Which tab owns an idle sign-out: this one, or another that got there first. */
export type IdleTimeoutSource = 'local' | 'remote';

/** Minimal storage surface the controller needs (one key). */
export interface IdleActivityStoreModel {
	read: () => string | null;
	write: (value: string) => void;
}

export interface IdleTimeoutControllerOptionsModel {
	/** Allowed inactivity, in milliseconds. */
	limitMs: number;
	store: IdleActivityStoreModel;
	/**
	 * The warning is up; the user will be signed out at `deadlineAt` (epoch ms)
	 * unless there is activity first.
	 */
	onWarning: (deadlineAt: number) => void;
	/** Activity arrived while the warning was up; hide it. */
	onActive: () => void;
	/**
	 * Sign the user out. `local`: this tab reached the limit and signs out,
	 * server revoke included. `remote`: another tab's `timedOut` mark
	 * arrived first, so this one only needs to follow.
	 */
	onTimeout: (source: IdleTimeoutSource) => void;
	now?: () => number;
	setTimer?: (callback: () => void, ms: number) => unknown;
	clearTimer?: (handle: unknown) => void;
	writeThrottleMs?: number;
}

/**
 * Warning lead for a limit: a minute, or half the limit when the limit is
 * shorter than two minutes (a one-minute limit would otherwise warn the
 * instant the user stops moving).
 */
export function computeWarningLeadMs(limitMs: number): number {
	return Math.min(MAX_WARNING_LEAD_MS, Math.floor(limitMs / 2));
}

/** Parse the stored record; anything malformed reads as absent. */
export function parseIdleActivityRecord(
	raw: string | null,
): IdleActivityRecordModel | null {
	if (raw == null) {
		return null;
	}
	try {
		const parsed: unknown = JSON.parse(raw);
		if (parsed == null || typeof parsed !== 'object') {
			return null;
		}
		const { at, timedOut } = parsed as { at?: unknown; timedOut?: unknown };
		if (typeof at !== 'number' || !Number.isFinite(at)) {
			return null;
		}
		return timedOut === true ? { at, timedOut: true } : { at };
	} catch {
		return null;
	}
}

export function serializeIdleActivityRecord(
	record: IdleActivityRecordModel,
): string {
	return JSON.stringify(record);
}

/**
 * Logout reason for an idle sign-out, carrying the limit so the sign-in
 * screen can say how long the user was away.
 */
export function buildIdleTimeoutReason(minutes: number): string {
	return `${IDLE_TIMEOUT_REASON}:${minutes}`;
}

export function isIdleTimeoutReason(reason: string | null): boolean {
	return (
		reason != null &&
		(reason === IDLE_TIMEOUT_REASON ||
			reason.startsWith(`${IDLE_TIMEOUT_REASON}:`))
	);
}

/** The minutes carried by an idle reason, or null when absent or unreadable. */
export function idleTimeoutMinutesFromReason(
	reason: string | null,
): number | null {
	if (reason == null || !reason.startsWith(`${IDLE_TIMEOUT_REASON}:`)) {
		return null;
	}
	const minutes = Number(reason.slice(IDLE_TIMEOUT_REASON.length + 1));
	return Number.isInteger(minutes) && minutes > 0 ? minutes : null;
}

/**
 * Whether a pointer move is a real one. Browsers dispatch synthetic
 * `mousemove` events when the page moves under a still cursor (after a
 * scroll or a layout change, such as the warning dialog opening) so hover
 * styles can update; those report the same screen position as the last
 * event and must not count as activity.
 */
export function isRealPointerMove(
	previous: { x: number; y: number } | null,
	next: { x: number; y: number },
): boolean {
	return previous == null || previous.x !== next.x || previous.y !== next.y;
}

/** Result of reading one idle-minutes box on the settings page. */
export interface IdleMinutesInputModel {
	/** The minutes to save; null when blank (off) or invalid. */
	minutes: number | null;
	/** Why the text cannot be saved, or null when it can. */
	error: 'not_whole_number' | 'out_of_range' | null;
}

/**
 * Read an idle-minutes box: blank is "off", otherwise a whole number of
 * minutes within the setting's bounds. Mirrors `IdleTimeoutSchema` so the
 * page can say what is wrong before the server refuses it.
 */
export function parseIdleMinutesInput(raw: string): IdleMinutesInputModel {
	const trimmed = raw.trim();
	if (trimmed === '') {
		return { minutes: null, error: null };
	}
	if (!/^\d+$/.test(trimmed)) {
		return { minutes: null, error: 'not_whole_number' };
	}
	const minutes = Number(trimmed);
	if (
		minutes < IDLE_TIMEOUT_MIN_MINUTES ||
		minutes > IDLE_TIMEOUT_MAX_MINUTES
	) {
		return { minutes: null, error: 'out_of_range' };
	}
	return { minutes, error: null };
}

/** What the app should do with its idle clock right now. */
export type IdleTrackingPlanModel =
	| { mode: 'wait' }
	| { mode: 'off' }
	| { mode: 'track'; limitMs: number };

/**
 * Turn "is someone signed in" and the tenant's configured minutes into a
 * plan. `undefined` minutes means the setting has not loaded yet, so nothing
 * happens either way. `null` means the tenant has the feature off: there is
 * no tracking and no warning, and any record left from when it was on is
 * cleared, so switching it back on later starts the clock fresh instead of
 * treating days of untracked use as idleness.
 */
export function planIdleTracking(
	isAuthenticated: boolean,
	limitMinutes: number | null | undefined,
): IdleTrackingPlanModel {
	if (!isAuthenticated || limitMinutes === undefined) {
		return { mode: 'wait' };
	}
	if (limitMinutes == null || !(limitMinutes > 0)) {
		return { mode: 'off' };
	}
	return { mode: 'track', limitMs: limitMinutes * 60_000 };
}

/**
 * How often a held clock rewrites the shared record: often enough that every
 * other tab sees fresh activity well before its warning would open (half the
 * time to the warning), and never more than every 30 seconds.
 */
export function computeHoldHeartbeatMs(limitMs: number): number {
	const untilWarning = limitMs - computeWarningLeadMs(limitMs);
	return Math.max(1_000, Math.min(30_000, Math.floor(untilWarning / 2)));
}

type ControllerState = 'stopped' | 'active' | 'warning' | 'timedOut';

/**
 * One app's idle clock. Create it when a signed-in user and a limit are both
 * known, `start()` it, feed it `recordActivity()` on real input and
 * `handleStorageChange()` on the record's storage events, and `stop()` it on
 * sign-out or when the limit changes.
 */
export class IdleTimeoutController {
	private readonly _limitMs: number;
	private readonly _warningLeadMs: number;
	private readonly _store: IdleActivityStoreModel;
	private readonly _callbacks: Pick<
		IdleTimeoutControllerOptionsModel,
		'onWarning' | 'onActive' | 'onTimeout'
	>;
	private readonly _now: () => number;
	private readonly _setTimer: (callback: () => void, ms: number) => unknown;
	private readonly _clearTimer: (handle: unknown) => void;
	private readonly _writeThrottleMs: number;

	private _state: ControllerState = 'stopped';
	private _timer: unknown = null;
	/** Latest activity this tab knows of, its own or another tab's. */
	private _lastActivityAt = 0;
	/** Something outside the clock counts as activity (see `setHold`). */
	private _held = false;

	public constructor(options: IdleTimeoutControllerOptionsModel) {
		this._limitMs = options.limitMs;
		this._warningLeadMs = computeWarningLeadMs(options.limitMs);
		this._store = options.store;
		this._callbacks = {
			onWarning: options.onWarning,
			onActive: options.onActive,
			onTimeout: options.onTimeout,
		};
		this._now = options.now ?? ((): number => Date.now());
		this._setTimer =
			options.setTimer ??
			((callback: () => void, ms: number): unknown => setTimeout(callback, ms));
		this._clearTimer =
			options.clearTimer ??
			((handle: unknown): void => {
				clearTimeout(handle as ReturnType<typeof setTimeout>);
			});
		this._writeThrottleMs =
			options.writeThrottleMs ?? IDLE_ACTIVITY_WRITE_THROTTLE_MS;
	}

	/**
	 * Begin tracking. A record older than the limit means this page is
	 * resuming a session nobody used for too long, so it signs out now. No
	 * record, or one a previous idle sign-out left behind, means a new
	 * session: the clock starts from this moment.
	 */
	public start(): void {
		if (this._state !== 'stopped') {
			return;
		}
		const now = this._now();
		const record = this._readRecord();
		if (record == null || record.timedOut === true || this._held) {
			this._state = 'active';
			this._writeActivity(now);
			this._evaluate();
			return;
		}
		this._lastActivityAt = record.at;
		this._state = 'active';
		this._evaluate();
	}

	/**
	 * Real user input. The deadline is checked before the activity is
	 * recorded: the first event after a laptop lid opens, or after a
	 * background tab comes back, must sign out rather than extend a session
	 * that already ran out.
	 *
	 * While the warning shows, only `deliberate` input counts (see
	 * `IdleActivityKindModel`); passive input just re-checks the deadline.
	 */
	public recordActivity(kind: IdleActivityKindModel = 'deliberate'): void {
		if (!this._isRunning()) {
			return;
		}
		if (this._state === 'warning' && kind === 'passive') {
			this._evaluate();
			return;
		}
		const now = this._now();
		// The warning is up: every event counts at once, so the dialog closes
		// on the first sign of life instead of up to five seconds later.
		if (
			this._state !== 'warning' &&
			now - this._lastActivityAt < this._writeThrottleMs
		) {
			return;
		}
		if (now - this._latestActivity() >= this._limitMs) {
			this._evaluate();
			return;
		}
		this._writeActivity(now);
		this._evaluate();
	}

	/**
	 * The shared record changed in another tab (`storage` event value).
	 * Removal means that tab signed out, which the auth layer propagates on
	 * its own; this clock just stops.
	 */
	public handleStorageChange(newValue: string | null): void {
		if (!this._isRunning()) {
			return;
		}
		const record = parseIdleActivityRecord(newValue);
		if (record == null) {
			this.stop();
			return;
		}
		if (record.timedOut === true) {
			this._timeOut('remote');
			return;
		}
		this._lastActivityAt = Math.max(this._lastActivityAt, record.at);
		this._evaluate();
	}

	public stop(): void {
		this._cancelTimer();
		this._state = 'stopped';
	}

	/**
	 * Hold the clock while something that is not input still means the user
	 * is there, such as a connected phone call (PD-16414). While held there is
	 * no warning and no sign-out, and the shared record is kept fresh so the
	 * other tabs do not time out either. Both edges count as activity, so the
	 * clock restarts from zero when the hold is released.
	 *
	 * The caller decides what holds the clock; this class knows nothing about
	 * phones. Safe to call before `start()`.
	 */
	public setHold(held: boolean): void {
		if (held === this._held) {
			return;
		}
		this._held = held;
		if (!this._isRunning()) {
			return;
		}
		this._writeActivity(this._now());
		this._evaluate();
	}

	private _isRunning(): boolean {
		return this._state === 'active' || this._state === 'warning';
	}

	/**
	 * Decide where the clock stands and schedule the next check. Timers in a
	 * background tab can fire late, which only ever makes this later, never
	 * earlier; `recordActivity` covers the tab coming back.
	 */
	private _evaluate(): void {
		if (!this._isRunning()) {
			return;
		}
		this._cancelTimer();
		const record = this._readRecord();
		if (record?.timedOut === true) {
			this._timeOut('remote');
			return;
		}
		if (this._held) {
			if (this._state === 'warning') {
				this._state = 'active';
				this._callbacks.onActive();
			}
			this._scheduleHeartbeat();
			return;
		}
		const lastActivity = this._latestActivity(record);
		const now = this._now();
		const idleFor = now - lastActivity;
		if (idleFor >= this._limitMs) {
			this._timeOut('local');
			return;
		}
		const warnAfter = this._limitMs - this._warningLeadMs;
		if (idleFor >= warnAfter) {
			if (this._state !== 'warning') {
				this._state = 'warning';
				this._callbacks.onWarning(lastActivity + this._limitMs);
			}
			this._schedule(lastActivity + this._limitMs - now);
			return;
		}
		if (this._state === 'warning') {
			this._state = 'active';
			this._callbacks.onActive();
		}
		this._schedule(lastActivity + warnAfter - now);
	}

	private _timeOut(source: IdleTimeoutSource): void {
		this._cancelTimer();
		this._state = 'timedOut';
		if (source === 'local') {
			// Mark before signing out so a tab that hears the mark before
			// reaching its own deadline follows instead of revoking.
			//
			// Not a lock: two tabs whose timers fire in the same instant each
			// write the mark before the other's arrives, and both revoke. That
			// is deliberately left alone. The tabs share one token pair, so the
			// second revoke is a no-op on tokens already revoked (both requests
			// answer 200). Making it strictly single would mean a settle delay
			// with a claim check before every idle sign-out, racing the
			// storefront's sign-out, which clears the shared session in every
			// tab at once.
			this._writeRecord({ at: this._lastActivityAt, timedOut: true });
		}
		this._callbacks.onTimeout(source);
	}

	/**
	 * The later of this tab's own knowledge and the shared record. A record
	 * that has vanished (another tab signing out) leaves this tab's own view
	 * standing until the auth layer catches up.
	 */
	private _latestActivity(
		record: IdleActivityRecordModel | null = this._readRecord(),
	): number {
		if (record != null) {
			this._lastActivityAt = Math.max(this._lastActivityAt, record.at);
		}
		return this._lastActivityAt;
	}

	private _writeActivity(at: number): void {
		this._lastActivityAt = at;
		this._writeRecord({ at });
	}

	/**
	 * Storage can refuse a write (private mode, quota). The clock keeps
	 * running on this tab's own view; only the cross-tab sharing is lost.
	 */
	private _writeRecord(record: IdleActivityRecordModel): void {
		try {
			this._store.write(serializeIdleActivityRecord(record));
		} catch {
			// Nothing actionable.
		}
	}

	private _readRecord(): IdleActivityRecordModel | null {
		try {
			return parseIdleActivityRecord(this._store.read());
		} catch {
			return null;
		}
	}

	private _schedule(ms: number): void {
		this._timer = this._setTimer(
			() => {
				this._timer = null;
				this._evaluate();
			},
			Math.max(0, ms),
		);
	}

	/**
	 * While held, refresh the shared record on a timer. Only this timer
	 * writes: re-evaluating on another tab's storage event never writes back,
	 * so two held tabs cannot echo writes to each other.
	 */
	private _scheduleHeartbeat(): void {
		this._timer = this._setTimer(() => {
			this._timer = null;
			if (this._held && this._isRunning()) {
				this._writeActivity(this._now());
			}
			this._evaluate();
		}, computeHoldHeartbeatMs(this._limitMs));
	}

	private _cancelTimer(): void {
		if (this._timer != null) {
			this._clearTimer(this._timer);
			this._timer = null;
		}
	}
}
